/**
 * Error Logs / Operations Service
 * Queries api_error_log and edge_function_log tables for the Operations dashboard
 */

import { supabase } from "./supabase";

export interface ApiErrorLog {
  id: string;
  endpoint: string;
  method: string;
  status_code: number;
  error_message: string;
  correlation_id: string | null;
  auth_type: string | null;
  api_key_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  request_context: Record<string, unknown> | null;
  created_at: string;
}

export interface ApiErrorFilters {
  endpoint?: string;
  statusCode?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface ApiErrorStats {
  errorsToday: number;
  errorsThisWeek: number;
  topErrorMessage: string | null;
  repeatedAuthFailureIps: { ip: string; count: number }[];
}

/**
 * Fetch API error logs with optional filters, paginated
 */
export const fetchApiErrors = async (
  filters: ApiErrorFilters = {},
  page: number = 1,
  pageSize: number = 25,
): Promise<{ data: ApiErrorLog[]; count: number }> => {
  let query = supabase
    .from("api_error_log")
    .select("*", { count: "exact" })
    .order("created_at", { ascending: false });

  if (filters.endpoint) {
    query = query.eq("endpoint", filters.endpoint);
  }
  if (filters.statusCode) {
    query = query.eq("status_code", filters.statusCode);
  }
  if (filters.dateFrom) {
    query = query.gte("created_at", filters.dateFrom);
  }
  if (filters.dateTo) {
    query = query.lte("created_at", `${filters.dateTo}T23:59:59.999Z`);
  }

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error("Failed to fetch API errors:", error);
    return { data: [], count: 0 };
  }

  return { data: (data as ApiErrorLog[]) || [], count: count || 0 };
};

/**
 * Fetch summary stats for the API Errors tab
 */
export const fetchApiErrorStats = async (): Promise<ApiErrorStats> => {
  const now = new Date();
  const todayStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).toISOString();
  const weekStart = new Date(
    now.getTime() - 7 * 24 * 60 * 60 * 1000,
  ).toISOString();

  // Errors today
  const { count: errorsToday } = await supabase
    .from("api_error_log")
    .select("*", { count: "exact", head: true })
    .gte("created_at", todayStart);

  // Errors this week
  const { count: errorsThisWeek } = await supabase
    .from("api_error_log")
    .select("*", { count: "exact", head: true })
    .gte("created_at", weekStart);

  // Most common error message this week
  const { data: recentErrors } = await supabase
    .from("api_error_log")
    .select("error_message")
    .gte("created_at", weekStart)
    .order("created_at", { ascending: false })
    .limit(200);

  let topErrorMessage: string | null = null;
  if (recentErrors && recentErrors.length > 0) {
    const counts: Record<string, number> = {};
    for (const e of recentErrors) {
      counts[e.error_message] = (counts[e.error_message] || 0) + 1;
    }
    let maxCount = 0;
    for (const [msg, count] of Object.entries(counts)) {
      if (count > maxCount) {
        maxCount = count;
        topErrorMessage = msg;
      }
    }
  }

  // Repeated auth failures: IPs with >5 401s in last 24h
  const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
  const { data: authFailures } = await supabase
    .from("api_error_log")
    .select("ip_address")
    .eq("status_code", 401)
    .gte("created_at", dayAgo);

  const repeatedAuthFailureIps: { ip: string; count: number }[] = [];
  if (authFailures && authFailures.length > 0) {
    const ipCounts: Record<string, number> = {};
    for (const row of authFailures) {
      if (row.ip_address) {
        ipCounts[row.ip_address] = (ipCounts[row.ip_address] || 0) + 1;
      }
    }
    for (const [ip, count] of Object.entries(ipCounts)) {
      if (count > 5) {
        repeatedAuthFailureIps.push({ ip, count });
      }
    }
    repeatedAuthFailureIps.sort((a, b) => b.count - a.count);
  }

  return {
    errorsToday: errorsToday || 0,
    errorsThisWeek: errorsThisWeek || 0,
    topErrorMessage,
    repeatedAuthFailureIps,
  };
};

/**
 * Fetch distinct endpoints for the filter dropdown
 */
export const fetchDistinctEndpoints = async (): Promise<string[]> => {
  const { data } = await supabase
    .from("api_error_log")
    .select("endpoint")
    .order("endpoint");

  if (!data) {
    return [];
  }

  const unique = [...new Set(data.map((d) => d.endpoint))];
  return unique;
};

// ============================================================================
// SYSTEM HEALTH — queries edge_function_log for operational metrics
// ============================================================================

export interface FunctionHealth {
  functionName: string;
  callsToday: number;
  callsThisWeek: number;
  errorCount: number;
  errorRate: number;
  avgResponseTimeMs: number;
  lastInvoked: string | null;
  status: "green" | "yellow" | "red";
}

export interface WebhookLiveness {
  functionName: string;
  lastReceived: string | null;
  status: "green" | "yellow" | "red";
}

export interface SlowestFunction {
  functionName: string;
  avgResponseTimeMs: number;
  callCount: number;
}

/**
 * Fetch health metrics for all instrumented edge functions
 */
export const fetchFunctionHealth = async (): Promise<FunctionHealth[]> => {
  const now = new Date();
  const todayStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).toISOString();
  const weekStart = new Date(
    now.getTime() - 7 * 24 * 60 * 60 * 1000,
  ).toISOString();

  // Get all logs from this week
  const { data: weekLogs } = await supabase
    .from("edge_function_log")
    .select("function_name, status_code, response_time_ms, created_at")
    .gte("created_at", weekStart)
    .order("created_at", { ascending: false });

  if (!weekLogs || weekLogs.length === 0) {
    return [];
  }

  // Group by function name
  const grouped: Record<
    string,
    {
      callsToday: number;
      callsThisWeek: number;
      errorCount: number;
      totalResponseTime: number;
      lastInvoked: string;
    }
  > = {};

  for (const log of weekLogs) {
    const fn = log.function_name;
    if (!grouped[fn]) {
      grouped[fn] = {
        callsToday: 0,
        callsThisWeek: 0,
        errorCount: 0,
        totalResponseTime: 0,
        lastInvoked: log.created_at,
      };
    }
    grouped[fn].callsThisWeek++;
    grouped[fn].totalResponseTime += log.response_time_ms;

    if (log.created_at >= todayStart) {
      grouped[fn].callsToday++;
    }
    if (log.status_code >= 400) {
      grouped[fn].errorCount++;
    }
    if (log.created_at > grouped[fn].lastInvoked) {
      grouped[fn].lastInvoked = log.created_at;
    }
  }

  const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();

  return Object.entries(grouped)
    .map(([functionName, stats]) => {
      const errorRate =
        stats.callsThisWeek > 0
          ? (stats.errorCount / stats.callsThisWeek) * 100
          : 0;
      const avgResponseTimeMs =
        stats.callsThisWeek > 0
          ? Math.round(stats.totalResponseTime / stats.callsThisWeek)
          : 0;

      let status: "green" | "yellow" | "red" = "green";
      if (errorRate > 10 || stats.lastInvoked < dayAgo) {
        status = "red";
      } else if (errorRate > 2) {
        status = "yellow";
      }

      return {
        functionName,
        callsToday: stats.callsToday,
        callsThisWeek: stats.callsThisWeek,
        errorCount: stats.errorCount,
        errorRate: Math.round(errorRate * 10) / 10,
        avgResponseTimeMs,
        lastInvoked: stats.lastInvoked,
        status,
      };
    })
    .sort((a, b) => {
      // Red first, then yellow, then green
      const statusOrder = { red: 0, yellow: 1, green: 2 };
      return statusOrder[a.status] - statusOrder[b.status];
    });
};

/**
 * Fetch webhook liveness (last received timestamps for webhook functions)
 */
export const fetchWebhookLiveness = async (): Promise<WebhookLiveness[]> => {
  const webhookFunctions = ["zuora-webhook", "sendgrid-webhook"];
  const now = new Date();
  const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
  const twoDaysAgo = new Date(
    now.getTime() - 48 * 60 * 60 * 1000,
  ).toISOString();

  const results: WebhookLiveness[] = [];

  for (const fn of webhookFunctions) {
    const { data } = await supabase
      .from("edge_function_log")
      .select("created_at")
      .eq("function_name", fn)
      .order("created_at", { ascending: false })
      .limit(1);

    const lastReceived = data && data.length > 0 ? data[0].created_at : null;
    let status: "green" | "yellow" | "red" = "green";

    if (!lastReceived || lastReceived < twoDaysAgo) {
      status = "red";
    } else if (lastReceived < dayAgo) {
      status = "yellow";
    }

    results.push({ functionName: fn, lastReceived, status });
  }

  return results;
};

/**
 * Fetch top 5 slowest functions by avg response time this week
 */
export const fetchSlowestFunctions = async (): Promise<SlowestFunction[]> => {
  const weekStart = new Date(
    Date.now() - 7 * 24 * 60 * 60 * 1000,
  ).toISOString();

  const { data } = await supabase
    .from("edge_function_log")
    .select("function_name, response_time_ms")
    .gte("created_at", weekStart);

  if (!data || data.length === 0) {
    return [];
  }

  const grouped: Record<string, { total: number; count: number }> = {};
  for (const log of data) {
    if (!grouped[log.function_name]) {
      grouped[log.function_name] = { total: 0, count: 0 };
    }
    grouped[log.function_name].total += log.response_time_ms;
    grouped[log.function_name].count++;
  }

  return Object.entries(grouped)
    .map(([functionName, stats]) => ({
      functionName,
      avgResponseTimeMs: Math.round(stats.total / stats.count),
      callCount: stats.count,
    }))
    .sort((a, b) => b.avgResponseTimeMs - a.avgResponseTimeMs)
    .slice(0, 5);
};
